import React, { useEffect, useState } from "react";
import { Alert, List, Modal, Progress, Tag } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone, LoadingOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { FRESHING_BOX_DELIVERY_LABELS, ORDER_STATUS_CANCELED } from "../../../../../constants/OrderConstant";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { sendOrderToFreshingBox } from "../../../../../functions/api/freshingbox";

const STATUS_PENDING = "pending";
const STATUS_RUNNING = "running";
const STATUS_SUCCESS = "success";
const STATUS_ERROR = "error";
const STATUS_SKIPPED = "skipped";

const BulkSendToFreshingBoxModal = ({ visible, onClose, orders }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (visible) {
      const initialItems = (orders || []).map((order) => ({
        order,
        status: STATUS_PENDING,
        error: null,
      }));
      setItems(initialItems);
      setIsDone(false);
      setIsRunning(false);
    }
  }, [visible, orders]);

  useEffect(() => {
    if (!visible || items.length === 0 || isRunning || isDone) return;

    const run = async () => {
      setIsRunning(true);
      const token = await getFirebaseIDToken();

      for (let i = 0; i < items.length; i++) {
        const { order } = items[i];

        const alreadySent = !!order.freshingBoxTrackingNumber;
        const isEligibleLabel = FRESHING_BOX_DELIVERY_LABELS.includes(order.deliveryLabel);
        const isCanceled = order.farmorderStatus === ORDER_STATUS_CANCELED;
        const hasCrates = Array.isArray(order.scannedCrates) && order.scannedCrates.length > 0;

        if (alreadySent || !isEligibleLabel || isCanceled) {
          setItems((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: STATUS_SKIPPED } : item)));
          continue;
        }

        if (!hasCrates) {
          setItems((prev) =>
            prev.map((item, idx) =>
              idx === i
                ? { ...item, status: STATUS_ERROR, error: t("notifications.freshingbox_requires_scanned_crates") }
                : item,
            ),
          );
          continue;
        }

        setItems((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: STATUS_RUNNING } : item)));

        try {
          const result = await sendOrderToFreshingBox(order, token);

          if (result.success) {
            setItems((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: STATUS_SUCCESS } : item)));
          } else {
            setItems((prev) =>
              prev.map((item, idx) => (idx === i ? { ...item, status: STATUS_ERROR, error: result.error } : item)),
            );
          }
        } catch (error) {
          setItems((prev) =>
            prev.map((item, idx) => (idx === i ? { ...item, status: STATUS_ERROR, error: error.message } : item)),
          );
        }
      }

      setIsRunning(false);
      setIsDone(true);
    };

    run();
  }, [visible, items, isRunning, isDone, t]);

  const successCount = items.filter((i) => i.status === STATUS_SUCCESS).length;
  const errorCount = items.filter((i) => i.status === STATUS_ERROR).length;
  const skippedCount = items.filter((i) => i.status === STATUS_SKIPPED).length;
  const doneCount = items.filter((i) => i.status !== STATUS_PENDING && i.status !== STATUS_RUNNING).length;
  const progressPercent = items.length > 0 ? Math.round((doneCount / items.length) * 100) : 0;

  const getOrderLabel = (order) => order.farmorderCustomerName || order.farmorderId || order.id;

  const renderStatusIcon = (status) => {
    switch (status) {
      case STATUS_SUCCESS:
        return <CheckCircleTwoTone twoToneColor="#52c41a" />;
      case STATUS_ERROR:
        return <CloseCircleTwoTone twoToneColor="#ff4d4f" />;
      case STATUS_SKIPPED:
        return <MinusCircleOutlined style={{ color: "#bfbfbf" }} />;
      case STATUS_RUNNING:
        return <LoadingOutlined style={{ color: "#1890ff" }} />;
      default:
        return <MinusCircleOutlined style={{ color: "#d9d9d9" }} />;
    }
  };

  const renderStatusTag = (status) => {
    switch (status) {
      case STATUS_SUCCESS:
        return <Tag color="green">{t("sent")}</Tag>;
      case STATUS_ERROR:
        return <Tag color="red">{t("error")}</Tag>;
      case STATUS_SKIPPED:
        return <Tag color="default">{t("skipped")}</Tag>;
      case STATUS_RUNNING:
        return <Tag color="blue">{t("sending")}</Tag>;
      default:
        return <Tag>{t("waiting")}</Tag>;
    }
  };

  const handleClose = () => {
    if (isRunning) return;
    onClose({ anySuccess: successCount > 0 });
  };

  return (
    <Modal
      title={t("send_shipments_to_freshingbox")}
      open={visible}
      onCancel={handleClose}
      closable={!isRunning}
      maskClosable={!isRunning}
      footer={null}
      width={560}
    >
      <Progress percent={progressPercent} status={errorCount > 0 && isDone ? "exception" : undefined} />

      {isDone && (
        <Alert
          style={{ marginTop: 12, marginBottom: 12 }}
          type={errorCount > 0 ? "warning" : "success"}
          showIcon
          message={t("notifications.freshingbox_bulk_result_summary", {
            success: successCount,
            error: errorCount,
            skipped: skippedCount,
          })}
        />
      )}

      <List
        style={{ marginTop: 12, maxHeight: 360, overflowY: "auto" }}
        dataSource={items}
        renderItem={(item) => (
          <List.Item>
            <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 8 }}>
              {renderStatusIcon(item.status)}
              <div style={{ flex: 1 }}>
                <div>{getOrderLabel(item.order)}</div>
                {item.status === STATUS_ERROR && item.error && (
                  <div style={{ fontSize: 12, color: "#ff4d4f" }}>{item.error}</div>
                )}
                {item.status === STATUS_SKIPPED && (
                  <div style={{ fontSize: 12, color: "#999" }}>{t("notifications.freshingbox_skip_reason")}</div>
                )}
              </div>
              {renderStatusTag(item.status)}
            </div>
          </List.Item>
        )}
      />
    </Modal>
  );
};

export default BulkSendToFreshingBoxModal;
